import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Alert,
    Collapse,
    Divider,
    Button,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    School as CourseIcon,
    MenuBook as LessonIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    SortByAlpha as SortIcon,
    CalendarToday as DateIcon,
    OpenInNew as OpenIcon,
    PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Note, noteService, UpdateNoteData } from '../../services/noteService';
import { exportService } from '../../services/exportService';
import NoteEditor from './NoteEditor';
import { useTranslation } from '../../i18n';

type SortOption = 'date' | 'title' | 'course';
type FilterOption = 'all' | 'withLesson';

interface GroupedNotes {
    courseId: number;
    courseTitle: string;
    courseAvailable: boolean;
    notes: Note[];
}

const NotesOverview: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('date');
    const [filterBy, setFilterBy] = useState<FilterOption>('all');
    const [expandedCourses, setExpandedCourses] = useState<Set<number>>(new Set());
    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        loadNotes();
    }, []);

    const loadNotes = async () => {
        try {
            setLoading(true);
            setError(null);
            const allNotes = await noteService.getNotes();
            setNotes(allNotes);
            // Expand all courses by default
            const courseIds = new Set(allNotes.filter(n => n.course).map(n => n.course!.id));
            setExpandedCourses(courseIds);
        } catch (err) {
            console.error('Error loading notes:', err);
            setError(t('notes.loadError', 'Fehler beim Laden der Notizen'));
        } finally {
            setLoading(false);
        }
    };

    const filteredNotes = useMemo(() => {
        let result = [...notes];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                (note) =>
                    note.title?.toLowerCase().includes(query) ||
                    note.content.toLowerCase().includes(query) ||
                    note.course?.title.toLowerCase().includes(query) ||
                    note.lesson?.title.toLowerCase().includes(query)
            );
        }

        // Apply type filter
        if (filterBy === 'withLesson') {
            result = result.filter((note) => note.lesson);
        }

        // Apply sorting
        result.sort((a, b) => {
            switch (sortBy) {
                case 'title':
                    return (a.title || '').localeCompare(b.title || '');
                case 'course':
                    return (a.course?.title || '').localeCompare(b.course?.title || '');
                case 'date':
                default:
                    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
            }
        });

        return result;
    }, [notes, searchQuery, sortBy, filterBy]);

    const groupedNotes = useMemo(() => {
        const groups: Map<number, GroupedNotes> = new Map();

        filteredNotes.forEach((note) => {
            if (!note.course) return;

            const courseId = note.course.id;
            if (!groups.has(courseId)) {
                groups.set(courseId, {
                    courseId,
                    courseTitle: note.course.title,
                    // Ältere API-Antworten ohne available-Flag: Kurs als verfügbar behandeln
                    courseAvailable: note.course.available !== false,
                    notes: [],
                });
            }
            groups.get(courseId)!.notes.push(note);
        });

        return Array.from(groups.values()).sort((a, b) =>
            a.courseTitle.localeCompare(b.courseTitle)
        );
    }, [filteredNotes]);

    const handleEdit = (note: Note) => {
        setEditingNote(note);
        setEditorOpen(true);
    };

    const handleDelete = async (note: Note) => {
        if (!window.confirm(t('notes.deleteConfirmMessage'))) return;

        try {
            await noteService.deleteNote(note.id);
            setNotes(notes.filter((n) => n.id !== note.id));
        } catch (err) {
            console.error('Error deleting note:', err);
        }
    };

    const handleSave = async (data: UpdateNoteData) => {
        if (!editingNote) return;

        try {
            const updated = await noteService.updateNote(editingNote.id, data);
            setNotes(notes.map((n) => (n.id === editingNote.id ? updated : n)));
        } catch (err) {
            console.error('Error updating note:', err);
            throw err;
        }
    };

    const toggleCourseExpanded = (courseId: number) => {
        const newExpanded = new Set(expandedCourses);
        if (newExpanded.has(courseId)) {
            newExpanded.delete(courseId);
        } else {
            newExpanded.add(courseId);
        }
        setExpandedCourses(newExpanded);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const navigateToCourse = (courseId: number, lessonId?: number) => {
        if (lessonId) {
            navigate(`/course-player/${courseId}?lesson=${lessonId}`);
        } else {
            navigate(`/course-player/${courseId}`);
        }
    };

    const handleExportPdf = async () => {
        try {
            setExporting(true);
            await exportService.downloadNotes();
        } catch (err) {
            console.error('Error exporting notes:', err);
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={40} />
                <Box sx={{ mt: 3 }}>
                    <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" height={150} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" height={150} sx={{ mb: 2 }} />
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                {t('notes.myNotes', 'Meine Notizen')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('notes.overviewDescription', 'Alle Ihre Notizen aus allen Kursen an einem Ort.')}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Search and Filter Bar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    placeholder={t('notes.search', 'Notizen durchsuchen...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1, minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />

                <Tooltip title={t('notes.sort', 'Sortieren')}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SortIcon />}
                        onClick={(e) => setSortAnchorEl(e.currentTarget)}
                    >
                        {sortBy === 'date' && t('notes.sortByDate', 'Datum')}
                        {sortBy === 'title' && t('notes.sortByTitle', 'Titel')}
                        {sortBy === 'course' && t('notes.sortByCourse', 'Kurs')}
                    </Button>
                </Tooltip>

                <Tooltip title={t('notes.filter', 'Filtern')}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterIcon />}
                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    >
                        {filterBy === 'all' && t('notes.filterAll', 'Alle')}
                        {filterBy === 'withLesson' && t('notes.filterWithLesson', 'Mit Lektion')}
                    </Button>
                </Tooltip>

                <Tooltip title={t('notes.exportPdf', 'Als PDF herunterladen')}>
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PdfIcon />}
                            onClick={handleExportPdf}
                            disabled={exporting || notes.length === 0}
                        >
                            {t('notes.exportPdfShort', 'PDF')}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {/* Sort Menu */}
            <Menu
                anchorEl={sortAnchorEl}
                open={Boolean(sortAnchorEl)}
                onClose={() => setSortAnchorEl(null)}
            >
                <MenuItem
                    selected={sortBy === 'date'}
                    onClick={() => {
                        setSortBy('date');
                        setSortAnchorEl(null);
                    }}
                >
                    <ListItemIcon>
                        <DateIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('notes.sortByDate', 'Nach Datum')}</ListItemText>
                </MenuItem>
                <MenuItem
                    selected={sortBy === 'title'}
                    onClick={() => {
                        setSortBy('title');
                        setSortAnchorEl(null);
                    }}
                >
                    <ListItemIcon>
                        <SortIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('notes.sortByTitle', 'Nach Titel')}</ListItemText>
                </MenuItem>
                <MenuItem
                    selected={sortBy === 'course'}
                    onClick={() => {
                        setSortBy('course');
                        setSortAnchorEl(null);
                    }}
                >
                    <ListItemIcon>
                        <CourseIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('notes.sortByCourse', 'Nach Kurs')}</ListItemText>
                </MenuItem>
            </Menu>

            {/* Filter Menu */}
            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => setFilterAnchorEl(null)}
            >
                <MenuItem
                    selected={filterBy === 'all'}
                    onClick={() => {
                        setFilterBy('all');
                        setFilterAnchorEl(null);
                    }}
                >
                    <ListItemText>{t('notes.filterAll', 'Alle Notizen')}</ListItemText>
                </MenuItem>
                <MenuItem
                    selected={filterBy === 'withLesson'}
                    onClick={() => {
                        setFilterBy('withLesson');
                        setFilterAnchorEl(null);
                    }}
                >
                    <ListItemIcon>
                        <LessonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('notes.filterWithLesson', 'Mit Lektion verknüpft')}</ListItemText>
                </MenuItem>
            </Menu>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                    label={`${filteredNotes.length} ${t('notes.notesLabel', 'Notizen')}`}
                    variant="outlined"
                    size="small"
                />
                <Chip
                    label={`${groupedNotes.length} ${t('notes.coursesLabel', 'Kurse')}`}
                    variant="outlined"
                    size="small"
                />
            </Box>

            {/* Notes List */}
            {filteredNotes.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchQuery
                                ? t('notes.noSearchResults', 'Keine Notizen gefunden')
                                : t('notes.noNotes', 'Noch keine Notizen vorhanden')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {searchQuery
                                ? t('notes.tryDifferentSearch', 'Versuchen Sie einen anderen Suchbegriff')
                                : t('notes.createFirstNote', 'Erstellen Sie Ihre erste Notiz in einem Kurs')}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                groupedNotes.map((group) => (
                    <Card key={group.courseId} sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                            onClick={() => toggleCourseExpanded(group.courseId)}
                        >
                            <CourseIcon color="primary" sx={{ mr: 2 }} />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {group.courseTitle}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {`${group.notes.length} ${t('notes.notesLabel', 'Notizen')}`}
                                </Typography>
                            </Box>
                            {group.courseAvailable ? (
                                <Tooltip title={t('notes.goToCourse', 'Zum Kurs')}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigateToCourse(group.courseId);
                                        }}
                                    >
                                        <OpenIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Chip
                                    label={t('notes.courseUnavailable', 'Kurs nicht mehr verfügbar')}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                />
                            )}
                            {expandedCourses.has(group.courseId) ? (
                                <ExpandLessIcon />
                            ) : (
                                <ExpandMoreIcon />
                            )}
                        </Box>

                        <Collapse in={expandedCourses.has(group.courseId)}>
                            <Divider />
                            {group.notes.map((note, index) => (
                                <React.Fragment key={note.id}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            pl: note.color ? 2 : 3,
                                            borderLeft: note.color
                                                ? `4px solid ${note.color}`
                                                : undefined,
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="subtitle2" fontWeight={500}>
                                                        {note.title || t('notes.untitled', 'Ohne Titel')}
                                                    </Typography>
                                                    {note.lesson && (
                                                        <Chip
                                                            icon={<LessonIcon sx={{ fontSize: 14 }} />}
                                                            label={note.lesson.title}
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={group.courseAvailable
                                                                ? () => navigateToCourse(group.courseId, note.lesson!.id)
                                                                : undefined}
                                                            sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        whiteSpace: 'pre-wrap',
                                                        mb: 1,
                                                    }}
                                                >
                                                    {note.content}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(note.updatedAt)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title={t('notes.edit', 'Bearbeiten')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(note)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('notes.delete', 'Löschen')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(note)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                    {index < group.notes.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </Collapse>
                    </Card>
                ))
            )}

            {/* Note Editor Dialog */}
            <NoteEditor
                open={editorOpen}
                onClose={() => {
                    setEditorOpen(false);
                    setEditingNote(null);
                }}
                onSave={handleSave}
                note={editingNote}
                courseId={editingNote?.course?.id}
                lessonId={editingNote?.lesson?.id}
            />
        </Box>
    );
};

export default NotesOverview;
